<template>
  <div class="index" />
</template>

<script>

/**
 * Vue: Index
 * -----------------------------------------------------------------------------
 * Used to set index states and global event listeners.
 *
 */
import { mapActions, mapState } from 'vuex'
import scrollDir from 'scrolldir'

import breakpoints from '~/config/breakpoints'
import { debounce, throttle } from '~/helpers/general'

import config from '~/../../canvas.config'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Index',

  data() {
    return {
      cnvsGlobal: true,
      screen: {
        breakpoint: 'xs',
        mobile: true,
        height: 0,
        width: 0,
        initialHeight: 0,
      },
      scroll: {
        direction: 'none',
        header: 'open',
        top: true,
        x: 0,
        y: 0,
      },
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      activeOverlays: (state) => state.overlays.active,
      settings: (state) => state.index.settings,
      isMobile: (state) => state.index.screen.mobile,
    }),
  },

  mounted() {
    this.rehydrateSite()
    this.setMobileBreakpoints(config.mobileBreakpoints)
    this.getPreviousUrl()

    this.handleResize(true)
    this.handleScroll()

    this.setEventListeners()
    this.setEventBusListeners()

    setTimeout(() => {
      this.handleStickyBehaviour()
    }, this.$timing('normal'))
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      rehydrateSite: 'rehydrateSite',
      setMobileBreakpoints: 'setMobileBreakpoints',
      setPreviousUrl: 'setPreviousUrl',
      setScreen: 'setScreen',
      setScroll: 'setScroll',
      setStylesheet: 'setStylesheet',
    }),

    /**
     * Get previous URL from sessionStorage.
     * - State doesn't update in time if set in the `beforeunload` event.
     */
    getPreviousUrl() {
      const previousUrl = sessionStorage.getItem('previousUrl')

      if (previousUrl) {
        this.setPreviousUrl(previousUrl)
      }
    },

    /**
     * Set event listeners.
     */
    setEventListeners() {
      this.setStylesheetListeners()

      /**
       * Add default listeners.
       */
      window.visualViewport.addEventListener(
        'resize',
        debounce(() => this.handleResize(), this.settings.throttle),
      )

      window.addEventListener(
        'scroll',
        throttle(() => this.handleStickyBehaviour(), 8),
      )
      document.addEventListener(
        'carousel-updated',
        () => this.handleStickyBehaviour(),
      )

      /**
       * Set previous URL just before changing page.
       */
      window.addEventListener('beforeunload', () => {
        sessionStorage.setItem('previousUrl', location.pathname)
      })
    },

    /**
     * Set EventBus listeners.
     */
    setEventBusListeners() {

      /**
       * Overlay listeners act as fallbacks for non-Vue components.
       */
      cnvs.EventBus.listen('overlay:open', (overlay) => {
        this.openOverlay(overlay)
      })

      cnvs.EventBus.listen('overlay:close', (overlay) => {
        this.closeOverlay(overlay)
      })
    },

    /**
     * Listen for stylesheets loading to trigger events after they've loaded.
     */
    setStylesheetListeners() {
      const stylesheets = [...document.querySelectorAll('[rel="stylesheet"]')]

      stylesheets.forEach((element) => {
        const media = element.getAttribute('media')
        let stylesheet = element.getAttribute('href')

        if (stylesheet?.includes('cdn.shopify')) {
          stylesheet = stylesheet
            .split('/')
            .reverse()[0]
            .split('?')[0]
        }

        if (!stylesheet) {
          return
        }

        /**
         * If it's already loaded then add to state.
         */
        if (media === 'all') {
          this.setStylesheet(stylesheet)
          return
        }

        /**
         * Otherwise listen for load.
         */
        element.addEventListener('load', () => {
          element.setAttribute('media', 'all')
          this.setStylesheet(stylesheet)
        })
      })
    },

    /**
     * Handle resize.
     * @param {Boolean} [start] - If setting on initial load.
     */
    handleResize(start = false) {
      this.screen.height = window.visualViewport.height
      this.screen.width = document.documentElement.clientWidth

      if (start) {
        this.screen.initialHeight = window.visualViewport.height

        document.documentElement.style.setProperty(
          '--initial-viewport-height',
          `${window.visualViewport.height}px`,
        )
      }

      /**
       * Set CSS variable.
       */
      document.documentElement.style.setProperty(
        '--viewport-height',
        `${this.screen.height}px`,
      )

      document.documentElement.style.setProperty(
        '--viewport-width',
        `${this.screen.width}px`,
      )

      for (const [breakpoint, value] of Object.entries(breakpoints)) {
        if (!window.matchMedia(`(min-width: ${value})`).matches) {
          break
        }

        this.screen.breakpoint = breakpoint
      }

      this.screen.mobile = this.settings.mobileBreakpoints.includes(this.screen.breakpoint)

      cnvs.EventBus.emit('cnvs:resize', this.screen)
      this.setScreen(this.screen)
    },

    handleScroll() {
      const siteHeader = document.querySelector('.site-header')
      if (!siteHeader) {
        return
      }

      const siteHeaderHeight = 72

      const scroll = () => {
        const currentScroll = window.scrollY

        if (currentScroll < siteHeaderHeight) {
          siteHeader.classList.remove('site-header--hidden')
          document.documentElement.removeAttribute('data-scrolldir')
        }
      }

      scrollDir({ dir: 'up' })

      const afterScroll = debounce(scroll, 200)

      window.addEventListener('scroll', afterScroll)

      siteHeader.addEventListener('transitionend', () => {
        const currentScroll = window.scrollY

        if (currentScroll > siteHeaderHeight) {
          siteHeader.classList.add('site-header--hidden')
        }
      })

      scroll()
    },

    /**
     * Callback from scroll event that handles the sticky behaviour.
     */
    handleStickyBehaviour() {
      if (this.isMobile) {
        this.handleParallaxSections()
        return
      }

      const parents = [...document.querySelectorAll('[js-sticky-parent]')]
      parents.forEach(parent => {
        this.scrollChildWithinParent(parent)
      })
    },

    /**
     * Function that handles section parallax behaviour on mobile.
     */
    handleParallaxSections() {
      const parents = [...document.querySelectorAll('[js-half-list="parent"]')]

      parents.forEach(parent => {
        if (!this.isParentVisible(parent)) {
          return
        }

        this.scrollParallaxSection(parent)
      })
    },

    /**
     * Scrolls the activated parallax sections on mobile. The element is
     * scrolled only within its parent.
     * @param {Object} parent - Parent element the child components are
     * within.
     */
    scrollParallaxSection(parent) {
      const children = [...parent.querySelectorAll('[js-half-list="child"]')]

      if (children?.length <= 1) {
        return
      }

      const firstChild = children[0]
      const lastChild = children[children.length - 1]

      if (!firstChild || !lastChild) {
        return
      }

      const viewport = this.screen.initialHeight
      const lastChildRect = lastChild.getBoundingClientRect()
      const firstChildRect = firstChild.getBoundingClientRect()

      firstChild.classList.add('parallax-parent')
      if (lastChildRect.bottom >= viewport && firstChildRect.bottom <= viewport) {
        firstChild.firstElementChild.classList.remove('parallax-child-absolute')
        firstChild.firstElementChild.classList.add('parallax-child-fixed')
      } else if (firstChildRect.top > 0) {
        firstChild.firstElementChild.classList.remove('parallax-child-fixed')
        firstChild.firstElementChild.classList.remove('parallax-child-absolute')
      } else {
        firstChild.firstElementChild.classList.remove('parallax-child-fixed')
        firstChild.firstElementChild.classList.add('parallax-child-absolute')
      }
    },

    /**
     * Helper function to check if the given parent element is visible.
     * - Augments the canvas standard helper function.
     * @param {Object} element - HTML DOM element.
     * @returns {Boolean}
     */
    isParentVisible(element) {
      const windowHeight = window.innerHeight
      const elementRectangle = element.getBoundingClientRect()
      const elementTop = elementRectangle.top
      const elementBottom = elementRectangle.bottom

      if ((elementTop >= 0) && (elementTop < windowHeight)) {
        return true
      }

      if ((elementTop < 0) && (elementBottom > windowHeight)) {
        return true
      }

      if ((elementTop < 0) && (elementBottom < windowHeight) && (elementBottom > 0)) {
        return true
      }

      return false
    },

    /**
     * Handles the scrolling of the child element within the given parent.
     * @param {Object} parent - HTML DOM element.
     */
    scrollChildWithinParent(parent) {
      const children = [...parent.querySelectorAll('[js-sticky-child=true]')]
      const parentRect = parent.getBoundingClientRect()
      const parentBottom = parentRect.bottom
      const parentTop = parentRect.top
      const parentOffset = parentBottom - this.screen.height
      const parentHeight = parent.offsetHeight

      children.forEach(child => {
        if (!child) {
          return
        }

        const childHeight = child.offsetHeight
        const childScroll = parentOffset + childHeight

        if (child.getAttribute('js-sticky-position') !== 'top') {
          if (parentOffset < 0) {
            child.classList.add('sticky-position--absolute')
            child.style.position = 'absolute'
            child.style.top = 'unset'
            child.style.bottom = '0px'
            child.classList.remove(this.$class('fixed'))
            return
          }

          if (childScroll >= parentHeight) {
            child.classList.add('sticky-position--absolute')
            child.style.position = 'absolute'
            child.style.bottom = 'unset'
            child.style.top = '0px'
            child.classList.remove(this.$class('fixed'))
            return
          }

          child.classList.remove('sticky-position--absolute')
          child.style.left = 'unset'
          child.style.position = 'fixed'
          child.style.top = 'unset'
          child.style.bottom = '0px'
          child.classList.add(this.$class('fixed'))
          return
        }

        // Logic for sticky top elements.
        if (parentTop < 0 && parentOffset > 0) {
          child.classList.remove('sticky-position--absolute')
          child.style.position = 'fixed'
          child.style.bottom = 'unset'
          child.style.top = '0px'
          child.classList.add(this.$class('fixed'))
          return
        }

        if (parentOffset <= 0) {
          child.classList.add('sticky-position--absolute')
          child.style.position = 'absolute'
          child.style.top = 'unset'
          child.style.bottom = '0px'
          child.classList.remove(this.$class('fixed'))
          return
        }

        child.classList.add('sticky-position--absolute')
        child.style.left = 'unset'
        child.style.position = 'absolute'
        child.style.bottom = 'unset'
        child.style.top = '0px'
        child.classList.remove(this.$class('fixed'))
      })
    },
  },
}
</script>
